import type { VariantStock } from '@/features/Product/services/productStock';
import type { BundleItem, ProductComplete } from '@/features/Product/types/models';
import type { ShopifyCartItem } from '@/features/Cart/types/servicesShopifyCart';
import type { CartItemQuantity } from '@/features/Cart/types/models';
import { getLineIdentity } from '@/features/Cart/services/lineItemProperties';

export type CartStockLine = Pick<ShopifyCartItem, 'key' | 'variant_id' | 'quantity' | 'properties'>;

export type StockLineGroups = {
  requestedLines: CartStockLine[];
  fixedLines: CartStockLine[];
  releasableLines: CartStockLine[];
};

type ExactStockGetter = (variantId: number) => number | undefined;

export type StockItem = {
  variantId: number;
  properties?: Record<string, string> | null;
};

export type AvailabilityContext = {
  getVariantStock: (variantId: number) => VariantStock | undefined;
  findProductBySku: (sku: string) => ProductComplete | null | undefined;
};

export const normaliseAvailabilityFlags = ({
  manualSoldOut,
  shopifyAvailable,
}: {
  manualSoldOut?: unknown;
  shopifyAvailable?: unknown;
}) => ({
  manualSoldOut: manualSoldOut === true || manualSoldOut === 'true',
  shopifyAvailable: shopifyAvailable !== false && shopifyAvailable !== 'false',
});

const isPositiveWholeNumber = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) > 0;

const getBundleComponentUnits = (bundleDetails: string): Map<number, number> | null => {
  try {
    const components: { id: string; quantity?: unknown }[] = JSON.parse(bundleDetails);
    return components.reduce((units, { id, quantity }) => {
      const variantId = Number(id.split('/').pop());
      if (!Number.isFinite(variantId)) return units;
      const componentUnits = isPositiveWholeNumber(quantity) ? quantity : 1;
      return units.set(variantId, (units.get(variantId) ?? 0) + componentUnits);
    }, new Map<number, number>());
  } catch {
    return null;
  }
};

export const getStockUnits = (
  variantId: number,
  properties?: Record<string, string> | null,
): Map<number, number> => {
  const bundleDetails = properties?._custom_bundle_details;
  return (bundleDetails && getBundleComponentUnits(bundleDetails)) ?? new Map([[variantId, 1]]);
};

export const getCartStockUsage = (
  cartItems: CartStockLine[],
  excludedLineKey?: string,
): Map<number, number> =>
  cartItems
    .filter((item) => item.key !== excludedLineKey)
    .reduce((usage, item) => {
      getStockUnits(item.variant_id, item.properties).forEach((units, variantId) =>
        usage.set(variantId, (usage.get(variantId) ?? 0) + units * item.quantity),
      );
      return usage;
    }, new Map<number, number>());

export const allocateCartStock = (
  cartItems: CartStockLine[],
  getExactStock: (variantId: number) => number | undefined,
): Map<string, number> => {
  const remainingStock = new Map<number, number>();
  const getRemaining = (variantId: number) =>
    remainingStock.get(variantId) ?? getExactStock(variantId) ?? 0;

  return cartItems.reduce((allocations, item) => {
    const trackedUnits = [...getStockUnits(item.variant_id, item.properties)].filter(
      ([variantId]) => getExactStock(variantId) !== undefined,
    );
    const allocatedQuantity = trackedUnits.reduce(
      (quantity, [variantId, units]) =>
        Math.min(quantity, Math.floor(Math.max(0, getRemaining(variantId)) / units)),
      item.quantity,
    );
    trackedUnits.forEach(([variantId, units]) =>
      remainingStock.set(variantId, getRemaining(variantId) - units * allocatedQuantity),
    );
    return allocations.set(item.key, allocatedQuantity);
  }, new Map<string, number>());
};

export const orderByStockPriority = <T extends CartStockLine>(
  lines: T[],
  protectedLineIdentities: ReadonlySet<string>,
): T[] => {
  const isProtected = ({ variant_id, properties }: T) =>
    protectedLineIdentities.has(getLineIdentity(variant_id, properties));
  return [...lines.filter(isProtected), ...lines.filter((line) => !isProtected(line))];
};

export const getConstrainedVariants = (
  requestedLines: CartStockLine[],
  getExactStock: ExactStockGetter,
): Set<number> =>
  new Set(
    [...getCartStockUsage(requestedLines).keys()].filter(
      (variantId) => getExactStock(variantId) !== undefined,
    ),
  );

const getUnreservedStock = (
  reservedLines: CartStockLine[],
  constrainedVariants: ReadonlySet<number>,
  getExactStock: ExactStockGetter,
): ExactStockGetter => {
  const reservedUsage = getCartStockUsage(reservedLines);
  return (variantId) => {
    const exactStock = getExactStock(variantId);
    if (exactStock === undefined || !constrainedVariants.has(variantId)) return undefined;
    return exactStock - (reservedUsage.get(variantId) ?? 0);
  };
};

export const planStockRelease = (
  { requestedLines, fixedLines, releasableLines }: StockLineGroups,
  constrainedVariants: ReadonlySet<number>,
  getExactStock: ExactStockGetter,
): CartItemQuantity[] | null => {
  const getUnreserved = getUnreservedStock(
    [...requestedLines, ...fixedLines],
    constrainedVariants,
    getExactStock,
  );
  const fitsWithoutReleasableLines = [...constrainedVariants].every(
    (variantId) => (getUnreserved(variantId) ?? 0) >= 0,
  );
  if (!fitsWithoutReleasableLines) return null;
  const allocations = allocateCartStock(releasableLines, getUnreserved);
  return releasableLines.flatMap(({ key, quantity }) => {
    const allocatedQuantity = allocations.get(key) ?? quantity;
    return allocatedQuantity < quantity ? [{ key, quantity: allocatedQuantity }] : [];
  });
};

export const getRestorableQuantities = (
  { requestedLines, fixedLines, releasableLines }: StockLineGroups,
  releases: CartItemQuantity[],
  constrainedVariants: ReadonlySet<number>,
  getExactStock: ExactStockGetter,
): Map<string, number> => {
  const releasedQuantities = new Map(releases.map(({ key, quantity }) => [key, quantity]));
  const keptLines = releasableLines.map((line) => ({
    ...line,
    quantity: releasedQuantities.get(line.key) ?? line.quantity,
  }));
  const releasedLines = releasableLines
    .filter(({ key }) => releasedQuantities.has(key))
    .map((line) => ({
      ...line,
      quantity: line.quantity - (releasedQuantities.get(line.key) ?? line.quantity),
    }));
  return allocateCartStock(
    releasedLines,
    getUnreservedStock(
      [...requestedLines, ...fixedLines, ...keptLines],
      constrainedVariants,
      getExactStock,
    ),
  );
};

const isVariantSoldOut = (
  { attributes }: ProductComplete,
  { getVariantStock }: AvailabilityContext,
): boolean => {
  const stock = getVariantStock(attributes.variantId);
  if (stock) return stock.quantity === 0;
  return attributes.manualSoldOut || attributes.shopifyAvailable === false;
};

type ChoiceGroupDemand = {
  requiredUnits: number;
  options: { variantId: number; maxUnits: number }[];
};

const canAllocateChoiceGroups = (
  demands: ChoiceGroupDemand[],
  getAvailableUnits: (variantId: number) => number,
): boolean => {
  const variantIds = [
    ...new Set(demands.flatMap(({ options }) => options.map(({ variantId }) => variantId))),
  ];
  const getVariantNode = (variantId: number) => demands.length + 1 + variantIds.indexOf(variantId);
  const sink = demands.length + variantIds.length + 1;
  const capacity = Array.from({ length: sink + 1 }, () => new Array<number>(sink + 1).fill(0));
  demands.forEach(({ requiredUnits, options }, index) => {
    capacity[0][index + 1] = requiredUnits;
    options.forEach(({ variantId, maxUnits }) => {
      capacity[index + 1][getVariantNode(variantId)] += maxUnits;
    });
  });
  variantIds.forEach((variantId) => {
    capacity[getVariantNode(variantId)][sink] = getAvailableUnits(variantId);
  });

  const findPath = (): number[] | null => {
    const previous = new Array<number>(sink + 1).fill(-1);
    previous[0] = 0;
    const queue = [0];
    while (queue.length) {
      const node = queue.shift() as number;
      for (let next = 1; next <= sink; next += 1) {
        if (previous[next] !== -1 || capacity[node][next] <= 0) continue;
        previous[next] = node;
        if (next === sink) return previous;
        queue.push(next);
      }
    }
    return null;
  };

  const requiredUnits = demands.reduce((total, demand) => total + demand.requiredUnits, 0);
  let allocatedUnits = 0;
  let path = findPath();
  while (path && allocatedUnits < requiredUnits) {
    const route = path;
    let units = Infinity;
    for (let node = sink; node !== 0; node = route[node]) {
      units = Math.min(units, capacity[route[node]][node]);
    }
    for (let node = sink; node !== 0; node = route[node]) {
      capacity[route[node]][node] -= units;
      capacity[node][route[node]] += units;
    }
    allocatedUnits += units;
    path = findPath();
  }
  return allocatedUnits >= requiredUnits;
};

const getRequiredUnits = ({ minSelection, isRequired }: BundleItem): number =>
  minSelection || (isRequired ? 1 : 0);

const isBundleContentsSoldOut = (
  bundle: ProductComplete,
  context: AvailabilityContext,
  isOptionItemSoldOut: (option: ProductComplete) => boolean,
): boolean => {
  const { bundleItems, additionalBundleDetails } = bundle.bundleDefinition;
  const randomisation = additionalBundleDetails?.randomisation;

  const isRandomisedOption = (option: ProductComplete): boolean =>
    !!randomisation?.productHandle && option.attributes.handle === randomisation.productHandle;

  const getExactUnits = (option: ProductComplete): number => {
    const stock = context.getVariantStock(option.attributes.variantId);
    return stock?.isExact ? stock.quantity : Infinity;
  };

  const getRandomisedUnits = (): number =>
    (randomisation?.options ?? []).reduce((total, { sku, maxQuantity }) => {
      const randomOption = context.findProductBySku(sku);
      if (!randomOption || isOptionItemSoldOut(randomOption)) return total;
      return total + Math.min(maxQuantity ?? 5, getExactUnits(randomOption));
    }, 0);

  const getOptionUnits = (option: ProductComplete): number =>
    isRandomisedOption(option) ? getRandomisedUnits() : getExactUnits(option);

  const isOptionSoldOut = (option: ProductComplete): boolean =>
    isRandomisedOption(option) ? getRandomisedUnits() === 0 : isOptionItemSoldOut(option);

  const fixedUnits = bundleItems
    .filter((item) => item.itemType === 'fixed')
    .flatMap((item) => (item.processedProducts ?? []).map((option) => ({ option, item })))
    .reduce(
      (units, { option, item }) =>
        units.set(
          option.attributes.variantId,
          (units.get(option.attributes.variantId) ?? 0) + (item.quantity || 1),
        ),
      new Map<number, number>(),
    );

  const getUnitsLeftAfterFixedItems = (option: ProductComplete): number =>
    getOptionUnits(option) - (fixedUnits.get(option.attributes.variantId) ?? 0);

  const isFixedItemSoldOut = bundleItems.some((item) => {
    if (item.itemType !== 'fixed') return false;
    const options = item.processedProducts ?? [];
    return (
      options.some(
        (option) => isOptionSoldOut(option) || getUnitsLeftAfterFixedItems(option) < 0,
      ) ||
      (!options.length && getRequiredUnits(item) > 0)
    );
  });
  if (isFixedItemSoldOut) return true;

  const choiceItems = bundleItems.filter(
    (item) => item.itemType === 'choice_group' && getRequiredUnits(item) > 0,
  );
  const choiceOptions = new Map(
    choiceItems
      .flatMap((item) => item.processedProducts ?? [])
      .map((option) => [option.attributes.variantId, option]),
  );
  const choiceDemands = choiceItems.map((item) => ({
    requiredUnits: getRequiredUnits(item),
    options: (item.processedProducts ?? [])
      .filter((option) => !isOptionSoldOut(option))
      .map((option) => ({
        variantId: option.attributes.variantId,
        maxUnits: option.attributes.maxCartQuantity || 5,
      })),
  }));

  return !canAllocateChoiceGroups(choiceDemands, (variantId) => {
    const option = choiceOptions.get(variantId);
    return option ? Math.max(0, getUnitsLeftAfterFixedItems(option)) : 0;
  });
};

const isItemSoldOut = (product: ProductComplete, context: AvailabilityContext): boolean => {
  if (!product.bundleDefinition?.bundleItems?.length) {
    return isVariantSoldOut(product, context);
  }
  return isBundleContentsSoldOut(product, context, (option) => isItemSoldOut(option, context));
};

export const resolveProductSoldOut = (
  product: ProductComplete,
  context: AvailabilityContext,
): boolean => {
  const variants = product.variantProducts ?? [];
  if (!variants.length) return isItemSoldOut(product, context);
  return variants.every((variant) => isItemSoldOut(variant, context));
};

export type AddStockItem = {
  id: number;
  quantity: number;
  properties?: Record<string, string> | null;
};

export type AddAvailabilityContext = {
  getVariantStock: (variantId: number) => VariantStock | undefined;
  findProductByVariantId: (variantId: number) => ProductComplete | null | undefined;
};

export const getAddStockDemand = (items: AddStockItem[]): Map<number, number> =>
  items.reduce((demand, { id, quantity, properties }) => {
    getStockUnits(id, properties).forEach((count, variantId) =>
      demand.set(variantId, (demand.get(variantId) ?? 0) + count * quantity),
    );
    return demand;
  }, new Map<number, number>());

export const findUnavailableAddVariants = (
  items: AddStockItem[],
  { getVariantStock, findProductByVariantId }: AddAvailabilityContext,
): number[] =>
  [...getAddStockDemand(items)]
    .filter(([variantId, quantity]) => {
      const stock = getVariantStock(variantId);
      if (stock) return stock.isExact && quantity > stock.quantity;
      const product = findProductByVariantId(variantId);
      return !!product?.attributes.manualSoldOut || product?.attributes.shopifyAvailable === false;
    })
    .map(([variantId]) => variantId);
